import {type ComponentType, lazy, type LazyExoticComponent} from 'react';

import {importWithRetry} from './dynamicImportRetry';

type AsyncLoader<T> = () => Promise<T>;

/**
 * Anything `React.lazy` accepts as an element type: a function component or
 * class, or one of the objects React wraps a component in (`memo`,
 * `forwardRef`), which it identifies by `$$typeof`. Everything else makes React
 * throw "Element type is invalid" — production error #306 — so this is what
 * tells a recovered module apart from an unrelated one.
 */
export function isComponentType(value: unknown): boolean {
  if (typeof value === 'function') {
    return true;
  }
  return typeof value === 'object' && value !== null && '$$typeof' in value;
}

/**
 * Whether a module exports the component a lazy loader expects: its default
 * export, or the one `selectComponent` picks. A selector written for the intended
 * module can throw on any other one, by reaching through exports that are not
 * there, which is itself proof the module is not the one asked for.
 */
export function isComponentModule<TModule>(
  module: unknown,
  selectComponent?: (module: TModule) => unknown
): boolean {
  if (selectComponent == null) {
    return (
      typeof module === 'object' &&
      module !== null &&
      'default' in module &&
      isComponentType(module.default)
    );
  }
  try {
    return isComponentType(selectComponent(module as TModule));
  } catch {
    return false;
  }
}

interface PreloadableLazyComponent<TModule, T extends ComponentType<any>> {
  Component: LazyExoticComponent<T>;
  /** Starts the module request early, so Suspense has less or nothing to wait for. */
  preload: AsyncLoader<TModule>;
}

/**
 * Memoizes one module load and allows a later call to retry after failure.
 * A transient chunk failure is retried under a cache-busted URL first, and
 * reloads the page as a last resort — see {@link importWithRetry}.
 */
export function createRetryableModuleLoader<T>(
  loadModule: AsyncLoader<T>,
  isExpectedModule?: (module: unknown) => boolean
): AsyncLoader<T> {
  let modulePromise: Promise<T> | undefined;

  return () => {
    modulePromise ??= importWithRetry(loadModule, {isExpectedModule}).catch(
      error => {
        modulePromise = undefined;
        throw error;
      }
    );
    return modulePromise;
  };
}

/**
 * The way to lazy-load a component: `React.lazy` alone turns a chunk fetch that
 * failed in transit into a full-page crash.
 *
 * Returns the component and a preload function sharing one module load, so a
 * hover or focus can start the request before the component renders.
 *
 * Pass the dynamic import at the call site so the bundler can discover its
 * literal path and keep the module in a separate chunk. `loadModule` must
 * perform exactly one import and return its module, a constraint recovery
 * imposes ({@link importWithRetry}); `selectComponent` covers a module that
 * exports the component under a name, and defaults to the default export.
 */
export function createPreloadableLazyComponent<T extends ComponentType<any>>(
  loadModule: AsyncLoader<{default: T}>
): PreloadableLazyComponent<{default: T}, T>;
export function createPreloadableLazyComponent<
  TModule,
  T extends ComponentType<any>,
>(
  loadModule: AsyncLoader<TModule>,
  selectComponent: (module: TModule) => T
): PreloadableLazyComponent<TModule, T>;
export function createPreloadableLazyComponent<
  TModule,
  T extends ComponentType<any>,
>(
  loadModule: AsyncLoader<TModule>,
  selectComponent?: (module: TModule) => T
): PreloadableLazyComponent<TModule, T> {
  // Recovery can resolve with a module the loader never asked for, whose
  // selected export is not a component. Checking it here sends that back through
  // recovery — which reloads, clearing the module map — instead of letting
  // `lazy` crash the app with error #306.
  const preload = createRetryableModuleLoader(loadModule, module =>
    isComponentModule(module, selectComponent)
  );
  const Component = lazy(async () => {
    const module = await preload();
    return selectComponent == null
      ? (module as {default: T})
      : {default: selectComponent(module)};
  });

  return {Component, preload};
}
